// Yoga Trowbridge - SEO-optimized location page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaTrowbridge = () => {
  usePageMeta({
    title: "Yoga Classes Trowbridge | Annelie Lily",
    description: "Yoga classes for Trowbridge, Wiltshire. Small group Hatha & Yin yoga in Frome, just 15 minutes from Trowbridge. Personal attention, all levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-trowbridge"
  });

  const locationSchema = generateLocationSchema({
    townName: "Trowbridge",
    description: "Yoga classes for Trowbridge at Annelie Lily Yoga in Frome. Small group classes and private sessions just 15 minutes from Trowbridge town centre.",
    url: "https://annelielily.com/yoga-trowbridge"
  });

  const faqs = [
    {
      question: "How long does it take to get to Frome from Trowbridge?",
      answer: "About 15 minutes via the A361. It's a straightforward drive through pleasant countryside — many students find the journey a nice way to decompress before class."
    },
    {
      question: "What yoga styles are available for Trowbridge residents?",
      answer: "I offer Hatha yoga (traditional practice with postures and breathwork), Yin yoga (slow, deep stretches), Aerial yoga (using a hammock), and private one-to-one sessions."
    },
    {
      question: "Do I need to be flexible to start yoga?",
      answer: "Not at all. Flexibility develops with practice. I offer modifications for every pose, so you can work at whatever level suits your body today."
    },
    {
      question: "How big are the classes?",
      answer: "Maximum 8 people. Small enough for personal attention, but with the energy of a group practice."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Trowbridge" currentSlug="areas/yoga-trowbridge" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              Yoga for Trowbridge
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Yoga Classes Trowbridge
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Looking for yoga classes near Trowbridge? My studio in Frome is just 15 minutes away — 
              close enough for a regular practice, far enough to feel like you've properly escaped.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <MapPin className="w-4 h-4 text-gold" />
                Based in Frome
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Car className="w-4 h-4 text-gold" />
                15 min from Trowbridge
              </span>
            </div>
            
            <Link to="/contact" className="btn-primary">
              Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Why Trowbridge Students Choose Frome
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Trowbridge has its own offerings, but if you're looking for something more intimate, 
                Frome is worth the short drive. My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
                capped at 8 people — small enough that I can actually see how you're moving and offer 
                guidance that's relevant to your body.
              </p>
              <p>
                The A361 makes it an easy journey, and many of my Trowbridge students say the drive 
                becomes part of the ritual — a chance to leave work behind before they even arrive.
              </p>
              <p>
                Whether you're interested in the more active <Link to="/areas/yoga-trowbridge/hatha-yoga" className="text-gold hover:underline">Hatha yoga near Trowbridge</Link> or 
                the slower, deeper work of <Link to="/areas/yoga-trowbridge/yin-yoga" className="text-gold hover:underline">Yin yoga in Trowbridge</Link>, you'll find a 
                welcoming space here. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">Complete beginners</Link> are always welcome.
              </p>
              <p>
                The main yoga styles I teach are <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha Yoga</Link> (active, strength-building) and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin Yoga</Link> (slower, deeply restorative).
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Available */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Yoga Classes for Trowbridge
            </h2>
            <p className="text-neutral-600">All classes held in Frome — 15 minutes from Trowbridge</p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga Trowbridge", desc: "Traditional practice combining postures, breathing, and relaxation. Good for building strength and flexibility.", link: "/areas/yoga-trowbridge/hatha-yoga" },
              { title: "Yin Yoga Trowbridge", desc: "Slow, deep stretches held for several minutes. Excellent for releasing tension and calming the mind.", link: "/areas/yoga-trowbridge/yin-yoga" },
              { title: "Private Sessions Trowbridge", desc: "One-to-one yoga designed around your needs. Ideal if you have specific goals or prefer individual attention.", link: "/areas/yoga-trowbridge/private-yoga" },
            ].map((service, index) => (
              <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Link to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all h-full group">
                  <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold transition-colors">{service.title}</h3>
                  <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                  <span className="text-gold text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-4 h-4" /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
            Questions from Trowbridge Students
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
            Get in touch and I'll help you find the right class for your schedule and experience level.
          </p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Other Areas */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also serving:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Bath", link: "/areas/yoga-bath" },
              { name: "Warminster", link: "/areas/yoga-warminster" },
              { name: "Westbury", link: "/areas/yoga-westbury" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Wells", link: "/areas/yoga-wells" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline underline-offset-4">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaTrowbridge;
